import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SetupAnalysisResult } from "@/types/agent/setup";

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "pt", label: "Portuguese" },
  { value: "it", label: "Italian" },
  { value: "nl", label: "Dutch" },
  { value: "pl", label: "Polish" },
  { value: "hi", label: "Hindi" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "zh", label: "Chinese (Mandarin)" },
];

interface ListenerSetupFormProps {
  onConfirm: (analysis: SetupAnalysisResult) => Promise<void>;
  onBack: () => void;
  isCreating: boolean;
}

export const ListenerSetupForm = ({ onConfirm, onBack, isCreating }: ListenerSetupFormProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [language, setLanguage] = useState("en");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const analysis: SetupAnalysisResult = {
      setupConversationId: "",
      genieName: name.trim(),
      useCase: description.trim() || "Meeting and call recording with transcription",
      language: LANGUAGE_OPTIONS.find((l) => l.value === language)?.label || "English",
      voicePreference: "Professional",
      conversationSettings: {
        maxDuration: 1800,
        turnTimeout: 30,
        silenceTimeout: 300,
      },
      systemPrompt: `You are ${name.trim()}, a Listener Genie that records and transcribes meetings.`,
      firstMessage: "Hello! I'm ready to record your meeting.",
      confidence: 1,
      agentType: "listener",
      category: "productivity",
      description: description.trim() || "Meeting and call recording with transcription",
    };

    await onConfirm(analysis);
  };

  return (
    <div className="h-screen w-full bg-[#F9FAFC] flex flex-col">
      {/* Top bar */}
      <div className="px-4 pt-3 md:px-6 md:pt-4">
        <Button
          onClick={onBack}
          variant="ghost"
          size="sm"
          className="text-gray-600 px-2 md:px-3 py-1 md:py-2 text-sm md:text-base min-w-0 h-8 md:h-9 flex items-center gap-1 md:gap-2 transition-colors duration-200 rounded-md hover:text-white hover:bg-gradient-to-r hover:from-[#4E9CFF] hover:to-[#FF4DB8]"
        >
          <ChevronLeft size={16} />
          <span className="hidden md:inline">Back</span>
        </Button>
      </div>

      {/* Centered content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="relative inline-flex items-center justify-center mb-3">
              {/* Soft brand glow so the mascot reads as summoned, not pasted */}
              <div
                aria-hidden
                className="absolute inset-0 m-auto h-24 w-24 rounded-full bg-gradient-to-r from-[#4E9CFF]/30 to-[#FF4DB8]/30 blur-2xl"
              />
              <img
                src="/genie-images/variants/mascot-whisper.png"
                alt="Listener Genie"
                className="relative h-28 w-auto object-contain drop-shadow-[0_10px_24px_rgba(78,156,255,0.25)]"
              />
            </div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-2">
              Set up your Listener Genie
            </h1>
            <p className="text-sm text-gray-500">
              Give it a name and tell it what kinds of meetings it will attend.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="listener-name" className="text-sm font-medium text-gray-700">
                Genie Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="listener-name"
                placeholder="e.g. Sales Call Listener, Team Meeting Recorder"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-10"
                required
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="listener-description" className="text-sm font-medium text-gray-700">
                What will it record? <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="listener-description"
                placeholder="e.g. Weekly sales team calls, client onboarding sessions, product demos..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="resize-none h-24"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="listener-language" className="text-sm font-medium text-gray-700">
                Primary Language
              </Label>
              <select
                id="listener-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <Button
              type="submit"
              disabled={!name.trim() || isCreating}
              className="w-full h-11 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white font-medium hover:opacity-90 transition-opacity border-0"
            >
              {isCreating ? "Creating Listener Genie…" : "Create Listener Genie"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
